import _ from 'lodash';

import SharedConfig from 'shared/config';

// The Raidbots environments as data (specs/admin-ops/05-env-model.md): which
// WoW realm each serves, which SimC branch and data domain, which raidbots
// branch the builder checks out, and whether it is on at all. ENV_TABLE is the
// compiled default; every field but live's realm and enabled can be overridden
// from the admin through ENV_<ENV>_<FIELD> metadata keys, plus BETA_SITE_ENV
// for which env web-beta serves. Unset or invalid means the table.
//
// Readers go through envs() / betaSiteEnv(). The services set the state from
// their config overlay after each refresh (shared-server/serverConfig); the
// browser reads what web wrote into window.config; the builder reads exported
// variables (inf/raidbotsEnv.js). Anything that must see a change *now* — the
// activate job, the admin routes — resolves from the metadata it just read.
//
// beta is off until the next expansion has a client. Until then web-beta
// (mimiron) serves test, explicitly, rather than by sharing test's pointer.
const DEFAULT_BETA_SITE_ENV = 'test';

export const ENV_TABLE = {
  live: {
    realm: 'live',
    simcBranch: 'midnight',
    simcDataDomain: 'live',
    simcChannel: 'latest',
    raidbotsBranch: 'main',
    enabled: true,
  },
  test: {
    realm: 'xptr',
    simcBranch: 'midnight',
    simcDataDomain: 'ptr',
    simcChannel: 'latest',
    raidbotsBranch: 'main',
    enabled: true,
  },
  beta: {
    realm: 'beta',
    simcBranch: 'midnight',
    simcDataDomain: 'live',
    simcChannel: 'beta',
    raidbotsBranch: 'main',
    enabled: false,
  },
};
export const ENV_KEYS = _.keys(ENV_TABLE);
export const BETA_SITE_ENV_KEY = 'BETA_SITE_ENV';

const REALM_KEYS = _.map(SharedConfig.wowEnvs, 'key');
// A plausible branch name: the characters allowed, no leading or trailing
// separator, no '..', no '.lock' (the VCS rules; the checkout on the builder is
// the only other place a bad one would surface).
const BRANCH_PATTERN = /^[\w.-][\w./-]{0,78}[\w-]$|^[\w-]$/;
const isBranchName = text => BRANCH_PATTERN.test(text) && !text.includes('..') && !text.endsWith('.lock');

// One row per field: the validator, the choices, and what the panel calls it.
export const FIELD_TYPES = {
  realm: {
    label: 'Realm',
    type: 'choice',
    // Never live: the live env owns that realm and its pointer.
    choices: _.without(REALM_KEYS, 'live'),
    help: 'WoW product the data is generated from: the data alias, icon prefix and Wowhead subdomain.',
  },
  simcBranch: {
    label: 'SimC branch',
    type: 'branch',
    help: 'Branch the data-update PR targets and the env\'s SimC build comes from.',
  },
  simcDataDomain: {
    label: 'Data domain',
    type: 'choice',
    choices: ['live', 'ptr'],
    help: 'ptr runs SimC with ptr=1, for a PTR client on the current branch. '
      + 'A new expansion\'s client is live on its own branch.',
  },
  simcChannel: {
    label: 'SimC channel',
    type: 'choice',
    choices: ['latest', 'nightly', 'weekly', 'beta'],
    help: 'The SimC channel the env\'s site forces. Read for the env web-beta serves.',
  },
  raidbotsBranch: {
    label: 'Raidbots branch',
    type: 'branch',
    help: 'Branch the builder checks out for a data run.',
  },
  enabled: {
    label: 'Enabled',
    type: 'bool',
    help: 'Off: no data runs, and web-beta cannot serve it.',
  },
};
export const FIELD_KEYS = _.keys(FIELD_TYPES);

export const settingKey = (env, field) => `ENV_${env.toUpperCase()}_${_.snakeCase(field).toUpperCase()}`;

// live is the live site: its realm and its being on are not settings.
const settableFields = env => (env === 'live' ? ['simcBranch', 'raidbotsBranch'] : FIELD_KEYS);

const asStored = (field, value) => {
  if (field === 'enabled') {
    return value ? '1' : '0';
  }
  return String(value);
};

// The typed table, one row per (env, field) plus the beta-site row: what the
// admin panel renders and what the metadata route validates against.
export const ENV_SETTINGS = [
  ..._.flatMap(ENV_KEYS, env => _.map(settableFields(env), field => ({
    key: settingKey(env, field),
    env,
    field,
    ...FIELD_TYPES[field],
    default: asStored(field, ENV_TABLE[env][field]),
  }))),
  {
    key: BETA_SITE_ENV_KEY,
    env: null,
    field: 'betaSiteEnv',
    label: 'web-beta serves',
    type: 'choice',
    choices: _.without(ENV_KEYS, 'live'),
    default: DEFAULT_BETA_SITE_ENV,
    help: 'The env the beta site serves: its pointer, realm, SimC channel and data domain.',
  },
];
const SETTINGS_BY_KEY = _.keyBy(ENV_SETTINGS, 'key');

export const envSettingByKey = key => SETTINGS_BY_KEY[key];

// Normalize an operator-entered string into what the readers accept. Returns
// { value } or { error }; null for a key that is not an env setting.
export const validateEnvSetting = (key, raw) => {
  const setting = SETTINGS_BY_KEY[key];
  if (!setting) {
    return null;
  }
  const text = _.trim(raw ?? '');
  if (setting.type === 'bool') {
    return text === '0' || text === '1' ? { value: text } : { error: 'must be 0 or 1' };
  }
  if (setting.type === 'choice') {
    if (_.includes(setting.choices, text)) {
      return { value: text };
    }
    return { error: `must be one of ${setting.choices.join(', ')}` };
  }
  return isBranchName(text) ? { value: text } : { error: 'must be a branch name' };
};

// The stored value as the readers use it, or undefined when the validator
// would refuse it (so the table's value stands).
const parseStored = (key, raw) => {
  const check = validateEnvSetting(key, raw);
  if (check.error) {
    return undefined;
  }
  return SETTINGS_BY_KEY[key].type === 'bool' ? check.value === '1' : check.value;
};

// lookup(key) -> the stored string or undefined. Returns { envs, betaSiteEnv }.
export const resolveEnvState = (lookup) => {
  const envs = _.mapValues(ENV_TABLE, (defaults, env) => {
    const overrides = {};
    _.each(settableFields(env), (field) => {
      const key = settingKey(env, field);
      const value = parseStored(key, lookup(key));
      if (value !== undefined) {
        overrides[field] = value;
      }
    });
    return { ...defaults, ...overrides };
  });
  // The beta site only ever serves an env that is on: the stored choice if
  // it is, else the default, else whatever non-live env is.
  const stored = parseStored(BETA_SITE_ENV_KEY, lookup(BETA_SITE_ENV_KEY));
  const candidates = _.compact([stored, DEFAULT_BETA_SITE_ENV, ..._.without(ENV_KEYS, 'live')]);
  const betaSiteEnv = _.find(candidates, env => envs[env].enabled) || DEFAULT_BETA_SITE_ENV;
  return { envs, betaSiteEnv };
};

const TABLE_STATE = { envs: ENV_TABLE, betaSiteEnv: DEFAULT_BETA_SITE_ENV };

let current = null;

export const setEnvState = (state) => {
  current = state;
};

// In the browser the source is what web wrote into the page, read lazily so
// no module has to run before another.
const browserState = () => (typeof window !== 'undefined' ? window.config?.envState : null);
const state = () => current || browserState() || TABLE_STATE;

export const envs = () => state().envs;
export const betaSiteEnv = () => state().betaSiteEnv;
export const betaSiteEnvSettings = () => envs()[betaSiteEnv()];

// Every enabled env generating from a realm: a hotfix for that realm is a run
// for each of them.
export const envsOnRealm = realm => _.keys(_.pickBy(envs(), settings => settings.enabled && settings.realm === realm));

// The wowEnvs row for a realm: product, display name, Wowhead subdomain.
export const realmInfo = realm => _.find(SharedConfig.wowEnvs, { key: realm });

// The realm the beta site serves right now — its Wowhead subdomain is what
// the site's tooltips and embeds link to.
export const betaSiteRealmInfo = () => realmInfo(betaSiteEnvSettings().realm);
